import {
  AnnotationOperationError,
  type AnnotationFrameMaterializer,
  type AnnotationImageRenderResource,
  type MaterializeAnnotationFrameRequest,
  type MaterializeAnnotationFrameResult,
  type ReplayableRenderSourceDescriptor,
} from "@carbon6/c6-frontend-design/annotation";
import {
  fetchAnnotationFrameBlob,
  getAnnotationSetFrame,
  type AnnotationRequestOptions,
  type AnnotationSetFrameView,
} from "@/api";
import {
  createAnnotationFrameDescriptor,
  decodeAnnotationImageSize,
  type AnnotationImageSize,
} from "../driver/annotationFrameDescriptor";
import { wireTargetToPoints } from "../model/annotationCoordinates";
import type { AnnotationFrameGeometryRegistry } from "../model/annotationCoordinates";
import { AnnotationIdentityRegistry } from "../model/annotationIdentity";
import { toAnnotationOperationError } from "../model/annotationOperationError";

type GetFrame = (
  setUuid: string,
  frameId: string,
  options: AnnotationRequestOptions,
) => Promise<AnnotationSetFrameView>;

type FetchFrame = (
  datasetUuid: string,
  camera: string,
  episode: string,
  frameIndex: number,
  options: AnnotationRequestOptions,
) => Promise<Blob>;

export class RegistryAnnotationFrameMaterializer implements AnnotationFrameMaterializer {
  constructor(
    private readonly options: {
      setUuid?: string;
      getSetUuid?: () => string | null;
      datasetUuid: string;
      identities: AnnotationIdentityRegistry;
      geometry?: AnnotationFrameGeometryRegistry;
      getFrame?: GetFrame;
      fetchFrame?: FetchFrame;
      decodeImageSize?: (
        blob: Blob,
        options: { signal: AbortSignal },
      ) => Promise<AnnotationImageSize>;
      createDescriptor?: (
        blob: Blob,
        options: { id: string; revision?: number },
      ) => ReplayableRenderSourceDescriptor<AnnotationImageRenderResource>;
    },
  ) {}

  async materializeFrame(
    request: MaterializeAnnotationFrameRequest,
    { signal }: { signal: AbortSignal },
  ): Promise<MaterializeAnnotationFrameResult> {
    try {
      if (signal.aborted) throw signal.reason;
      if (request.frame.revision !== request.expectedRevision) {
        throw this.revisionConflict();
      }
      const identity = this.options.identities.frame(request.frame.frameId);
      if (identity === null) throw new Error("unknown annotation frame identity");
      if (
        request.frame.sequenceId !== this.options.identities.sequenceId(identity.episode) ||
        request.frame.streamId !== this.options.identities.streamId(identity.camera)
      ) {
        throw new Error("annotation frame locator identity mismatch");
      }

      const setUuid = this.options.getSetUuid?.() ?? this.options.setUuid ?? null;
      if (setUuid === null) throw new Error("annotation set has not been created");
      const getFrame = this.options.getFrame ?? getAnnotationSetFrame;
      const fetchFrame = this.options.fetchFrame ?? fetchAnnotationFrameBlob;
      const [detail, blob] = await Promise.all([
        getFrame(setUuid, request.frame.frameId, { signal }),
        fetchFrame(
          this.options.datasetUuid,
          identity.camera,
          identity.episode,
          identity.frameIndex,
          { signal },
        ),
      ]);
      if (signal.aborted) throw signal.reason;
      this.assertCurrentDetail(request, detail);

      const decodeImageSize = this.options.decodeImageSize ?? decodeAnnotationImageSize;
      const size = await decodeImageSize(blob, { signal });
      if (signal.aborted) throw signal.reason;
      this.options.geometry?.bind(request.frame.frameId, size);
      const createDescriptor = this.options.createDescriptor ?? createAnnotationFrameDescriptor;
      const frameSource = createDescriptor(blob, {
        id: `${request.frame.frameId}:source`,
        revision: 1,
      });
      const targets = Object.fromEntries(
        detail.targets.map((target) => {
          const targetId = this.options.identities.targetId(target.obj_id);
          return [
            targetId,
            {
              targetId,
              points: wireTargetToPoints(target, size, `${request.frame.frameId}:${targetId}`),
            },
          ];
        }),
      );

      return {
        revision: detail.revision,
        frame: {
          frameId: detail.frame_id,
          sequenceId: request.frame.sequenceId,
          streamId: request.frame.streamId,
          timestampSeconds: detail.timestamp_seconds,
          sourceRevision: 1,
          width: size.width,
          height: size.height,
          frameSource,
          targets,
        },
      };
    } catch (error) {
      throw toAnnotationOperationError("render", error);
    }
  }

  private assertCurrentDetail(
    request: MaterializeAnnotationFrameRequest,
    detail: AnnotationSetFrameView,
  ): void {
    const identity = this.options.identities.frame(request.frame.frameId);
    if (
      detail.frame_id !== request.frame.frameId ||
      detail.revision !== request.expectedRevision ||
      identity === null ||
      detail.camera !== identity.camera ||
      detail.episode !== identity.episode ||
      detail.frame_index !== identity.frameIndex
    ) {
      throw this.revisionConflict();
    }
  }

  private revisionConflict(): AnnotationOperationError {
    return new AnnotationOperationError("render", "training.annotation_frame_revision_conflict", {
      localizedUserMessage: {
        en: "The saved frame changed while it was loading.",
        ko: "저장된 프레임을 불러오는 동안 내용이 변경되었습니다.",
      },
      retryable: true,
      conflict: true,
    });
  }
}
